import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Check, Trash2, Settings, X } from "lucide-react";
import { updateBadgeSource } from "@/utils/notifications";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== "dive_center") {
        setUser(parsedUser);
      }
    }
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;

      if (isMobile) {
        // Center the dropdown on mobile, cap vertical position
        const dropdownWidth = Math.min(320, window.innerWidth - 16);
        const maxTop = Math.min(rect.bottom + 8, window.innerHeight * 0.15);
        setDropdownPos({
          top: maxTop,
          left: Math.max(8, (window.innerWidth - dropdownWidth) / 2),
          right: 'auto',
          width: dropdownWidth,
        });
      } else {
        setDropdownPos({
          top: rect.bottom + 8,
          right: Math.max(8, window.innerWidth - rect.right),
          left: 'auto',
          width: undefined,
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Update PWA badge when unread count changes
  useEffect(() => {
    updateBadgeSource('notifications', unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the button and dropdown
      const isOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      if (isOutsideButton && isOutsideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const deleted = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deleted && !deleted.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Failed to delete notification:", err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id, { stopPropagation: () => {} });
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "forum_reply": return "💬";
      case "forum_mention": return "@";
      case "chat_mention": return "📢";
      case "thread_reaction": return "🐟";
      case "reply_reaction": return "🐟";
      case "report_shared": return "📋";
      case "storage_alert": return "☁️";
      default: return "🔔";
    }
  };

  if (!user) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-80 sm:w-96 bg-white rounded-lg shadow-xl border z-[10200] max-h-[70vh] overflow-hidden flex flex-col"
          style={{
            top: `${dropdownPos.top}px`,
            ...(dropdownPos.left !== 'auto' ? { left: `${dropdownPos.left}px` } : {}),
            ...(dropdownPos.right !== 'auto' ? { right: `${dropdownPos.right}px` } : {}),
            ...(dropdownPos.width ? { width: `${dropdownPos.width}px` } : {}),
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                >
                  <Check className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => { setIsOpen(false); navigate("/account?tab=notifications"); }}
                className="p-1 hover:bg-gray-200 rounded"
                title="Notification Settings"
              >
                <Settings className="w-4 h-4 text-gray-500" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-200 rounded"
                title="Close"
                data-testid="close-notifications-btn"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-3 py-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                    !notification.is_read ? "bg-cyan-50" : ""
                  }`}
                >
                  {/* Mobile-optimized layout */}
                  <div className="flex items-start gap-2">
                    {/* Icon or Avatar - smaller on mobile */}
                    <div className="flex-shrink-0">
                      {notification.from_user_pic ? (
                        <img 
                          src={getImageUrl(notification.from_user_pic)} 
                          alt="" 
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-sm">
                          {getNotificationIcon(notification.type)}
                        </div>
                      )}
                    </div>
                    
                    {/* Content - flexible width with proper overflow */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className={`text-sm leading-tight ${!notification.is_read ? "font-semibold" : ""}`}>
                        {notification.title}
                      </p>
                      {/* Message with horizontal scroll on overflow */}
                      <p className="text-sm text-gray-600 mt-0.5 whitespace-nowrap overflow-x-auto scrollbar-none">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    
                    {/* Actions - compact on mobile */}
                    <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                      {!notification.is_read && (
                        <button
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          className="p-1.5 hover:bg-gray-200 rounded text-gray-400 hover:text-green-600"
                          title="Mark as read"
                        >
                          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(notification.id, e)}
                        className="p-1.5 hover:bg-gray-200 rounded text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
